import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Search, 
  X, 
  Briefcase, 
  FileText, 
  Target, 
  Building2, 
  Wrench, 
  Users, 
  ClipboardList,
  Loader2,
  Calendar,
  ChevronRight,
  UserCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category configuration
const CATEGORIES = [
  { id: 'jobs', label: 'Jobs', icon: Briefcase, color: 'bg-blue-100 text-blue-700' },
  { id: 'proposals', label: 'Proposals', icon: FileText, color: 'bg-purple-100 text-purple-700' },
  { id: 'opportunities', label: 'Opportunities', icon: Target, color: 'bg-amber-100 text-amber-700' },
  { id: 'contacts', label: 'Companies', icon: Building2, color: 'bg-green-100 text-green-700' },
  { id: 'people', label: 'Contacts', icon: UserCircle, color: 'bg-cyan-100 text-cyan-700' },
  { id: 'equipment', label: 'Equipment', icon: Wrench, color: 'bg-slate-100 text-slate-700' },
  { id: 'team', label: 'Team Members', icon: Users, color: 'bg-pink-100 text-pink-700' },
  { id: 'field_reports', label: 'Field Reports', icon: ClipboardList, color: 'bg-orange-100 text-orange-700' },
];

// Status options per category
const STATUS_OPTIONS = {
  jobs: ['Pending', 'Scheduled', 'In Progress', 'Completed', 'Cancelled'],
  proposals: ['Draft', 'Sent', 'Accepted', 'Lost', 'Expired'],
  opportunities: ['New', 'Contacted', 'Qualified', 'Proposal Sent', 'Won', 'Lost'],
  contacts: [],
  people: [],
  equipment: ['Active', 'In Maintenance', 'Out of Service', 'Retired'],
  team: ['Active', 'Inactive'],
  field_reports: ['Draft', 'Submitted', 'Approved'],
};

// Date preset options
const DATE_PRESETS = [
  { label: 'Any Time', value: 'any' },
  { label: 'Today', value: 'today' },
  { label: 'Last 7 Days', value: '7days' },
  { label: 'Last 30 Days', value: '30days' },
  { label: 'This Month', value: 'this_month' },
  { label: 'This Quarter', value: 'this_quarter' },
  { label: 'This Year', value: 'this_year' },
  { label: 'Custom Range', value: 'custom' },
];

export default function AdvancedSearchModal({ open, onOpenChange }) {
  const navigate = useNavigate();
  
  // Search state
  const [query, setQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState(
    CATEGORIES.reduce((acc, cat) => ({ ...acc, [cat.id]: true }), {})
  );
  const [datePreset, setDatePreset] = useState('any');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [status, setStatus] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [teamMemberFilter, setTeamMemberFilter] = useState('');
  
  // Data state
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  
  // Load clients and team members for filters
  useEffect(() => {
    if (open) {
      loadFilterData();
    }
  }, [open]);
  
  const loadFilterData = async () => {
    try {
      const [clientsRes, teamRes] = await Promise.all([
        axios.get(`${API_URL}/api/contacts`),
        axios.get(`${API_URL}/api/team`)
      ]);
      setClients(clientsRes.data || []);
      setTeamMembers(teamRes.data || []);
    } catch (error) {
      console.error('Failed to load filter data:', error);
    }
  };
  
  // Calculate date range from preset
  const getDateRange = useCallback(() => {
    const now = new Date();
    let from = null;
    let to = null;
    
    switch (datePreset) {
      case 'today':
        from = new Date(now.setHours(0, 0, 0, 0));
        to = new Date();
        break;
      case '7days':
        from = new Date(now.setDate(now.getDate() - 7));
        to = new Date();
        break;
      case '30days':
        from = new Date(now.setDate(now.getDate() - 30));
        to = new Date();
        break;
      case 'this_month':
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        to = new Date();
        break;
      case 'this_quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        from = new Date(now.getFullYear(), quarter * 3, 1);
        to = new Date();
        break;
      case 'this_year':
        from = new Date(now.getFullYear(), 0, 1);
        to = new Date();
        break;
      case 'custom':
        from = dateFrom ? new Date(dateFrom) : null;
        to = dateTo ? new Date(dateTo) : null;
        break;
      default:
        break;
    }
    
    return { from, to };
  }, [datePreset, dateFrom, dateTo]);
  
  // Perform search
  const handleSearch = async () => {
    if (!query && !status && !clientFilter && !teamMemberFilter && datePreset === 'any') {
      return; // Need at least some criteria
    }
    
    setLoading(true);
    try {
      const { from, to } = getDateRange();
      const categories = Object.entries(selectedCategories)
        .filter(([_, selected]) => selected)
        .map(([id]) => id);
      
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      if (categories.length < CATEGORIES.length) {
        params.append('categories', categories.join(','));
      }
      if (from) params.append('date_from', from.toISOString());
      if (to) params.append('date_to', to.toISOString());
      if (status) params.append('status', status);
      if (clientFilter) params.append('client_id', clientFilter);
      if (teamMemberFilter) params.append('team_member_id', teamMemberFilter);
      
      const response = await axios.get(`${API_URL}/api/search/advanced?${params.toString()}`);
      setResults(response.data);
    } catch (error) {
      console.error('Advanced search failed:', error);
      // Fallback to basic search if advanced endpoint doesn't exist
      if (query && query.length >= 2) {
        try {
          const response = await axios.get(`${API_URL}/api/search?q=${encodeURIComponent(query)}`);
          setResults(response.data);
        } catch (e) {
          console.error('Basic search also failed:', e);
        }
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Handle category toggle
  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };
  
  // Select/deselect all categories
  const toggleAllCategories = (selectAll) => {
    setSelectedCategories(
      CATEGORIES.reduce((acc, cat) => ({ ...acc, [cat.id]: selectAll }), {})
    );
  };
  
  // Navigate to result
  const handleResultClick = (item) => {
    onOpenChange(false);
    
    switch (item.type) {
      case 'job':
        navigate(`/jobs/${item.id}`);
        break;
      case 'proposal':
        navigate(`/proposals/${item.id}/preview`);
        break;
      case 'opportunity':
        navigate('/opportunities');
        break;
      case 'contact':
        navigate('/companies');
        break;
      case 'person':
        navigate('/contacts');
        break;
      case 'equipment':
        navigate('/equipment');
        break;
      case 'team':
        navigate('/team');
        break;
      case 'field_report':
        if (item.job_id) {
          navigate(`/jobs/${item.job_id}`);
        }
        break;
      default:
        break;
    }
  };
  
  // Reset filters
  const resetFilters = () => {
    setQuery('');
    setSelectedCategories(CATEGORIES.reduce((acc, cat) => ({ ...acc, [cat.id]: true }), {}));
    setDatePreset('any');
    setDateFrom('');
    setDateTo('');
    setStatus('');
    setClientFilter('');
    setTeamMemberFilter('');
    setResults(null);
  };
  
  // Get available statuses based on selected categories
  const getAvailableStatuses = () => {
    const selected = Object.entries(selectedCategories)
      .filter(([_, isSelected]) => isSelected)
      .map(([id]) => id);
    
    const statuses = new Set();
    selected.forEach(cat => {
      (STATUS_OPTIONS[cat] || []).forEach(s => statuses.add(s));
    });
    
    return Array.from(statuses);
  };
  
  // Count results per category
  const getResultCount = (categoryId) => {
    if (!results) return 0;
    const data = results[categoryId];
    return Array.isArray(data) ? data.length : 0;
  };
  
  const totalResults = results ? 
    CATEGORIES.reduce((sum, cat) => sum + getResultCount(cat.id), 0) : 0;
  
  // "Results (N)" header uses the true per-category totals (matches the
  // "Showing X of Y" lines), not just the displayed row count.
  const trueTotalResults = results?.total_counts
    ? CATEGORIES.reduce((sum, cat) => sum + (results.total_counts[cat.id] ?? getResultCount(cat.id)), 0)
    : totalResults;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="w-5 h-5" />
            Advanced Search
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          {/* Search Input */}
          <div className="space-y-2">
            <Label>Search Keywords</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Enter search terms..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1"
                data-testid="advanced-search-input"
              />
              <Button onClick={handleSearch} disabled={loading} data-testid="advanced-search-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Search
              </Button>
            </div>
          </div>
          
          {/* Categories */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Search In</Label>
              <div className="flex gap-2 text-xs">
                <button 
                  className="text-amber-600 hover:underline"
                  onClick={() => toggleAllCategories(true)}
                >
                  Select All
                </button>
                <span className="text-slate-300">|</span>
                <button 
                  className="text-slate-500 hover:underline"
                  onClick={() => toggleAllCategories(false)}
                >
                  Clear All
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(cat => {
                const Icon = cat.icon;
                const isSelected = selectedCategories[cat.id];
                return (
                  <button
                    key={cat.id}
                    onClick={() => toggleCategory(cat.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border ${
                      isSelected 
                        ? 'bg-blue-100 text-blue-700 border-blue-300' 
                        : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-slate-100'
                    }`}
                    data-testid={`category-${cat.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    {cat.label}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Filters Row - All 4 filters in ONE horizontal row */}
          <div className="flex flex-wrap sm:flex-nowrap gap-3 items-end">
            {/* Date Range */}
            <div className="flex-1 min-w-[140px]">
              <Label className="block mb-1.5">Date Range</Label>
              <Select value={datePreset} onValueChange={setDatePreset}>
                <SelectTrigger data-testid="date-preset-select" className="h-10">
                  <SelectValue placeholder="Select date range" />
                </SelectTrigger>
                <SelectContent>
                  {DATE_PRESETS.map(preset => (
                    <SelectItem key={preset.value} value={preset.value}>
                      {preset.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Status Filter */}
            <div className="flex-1 min-w-[140px]">
              <Label className="block mb-1.5">Status</Label>
              <Select value={status || "any_status"} onValueChange={(v) => setStatus(v === "any_status" ? "" : v)}>
                <SelectTrigger data-testid="status-select" className="h-10">
                  <SelectValue placeholder="Any status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any_status">Any Status</SelectItem>
                  {getAvailableStatuses().map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Client Filter */}
            <div className="flex-1 min-w-[140px]">
              <Label className="block mb-1.5">Client/Company</Label>
              <Select value={clientFilter || "any_client"} onValueChange={(v) => setClientFilter(v === "any_client" ? "" : v)}>
                <SelectTrigger data-testid="client-select" className="h-10">
                  <SelectValue placeholder="Any client" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any_client">Any Client</SelectItem>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.company_name || c.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Team Member Filter */}
            <div className="flex-1 min-w-[140px]">
              <Label className="block mb-1.5">Team Member</Label>
              <Select value={teamMemberFilter || "any_team"} onValueChange={(v) => setTeamMemberFilter(v === "any_team" ? "" : v)}>
                <SelectTrigger data-testid="team-member-select" className="h-10">
                  <SelectValue placeholder="Any team member" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any_team">Any Team Member</SelectItem>
                  {teamMembers.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Custom Date Range - Shown below when selected */}
          {datePreset === 'custom' && (
            <div className="grid grid-cols-2 gap-4 max-w-xs">
              <div className="space-y-1">
                <Label className="text-xs">From</Label>
                <Input 
                  type="date" 
                  value={dateFrom} 
                  onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="date-from-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">To</Label>
                <Input 
                  type="date" 
                  value={dateTo} 
                  onChange={(e) => setDateTo(e.target.value)}
                  data-testid="date-to-input"
                />
              </div>
            </div>
          )}
          
          {/* Reset Button */}
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={resetFilters}>
              <X className="w-4 h-4 mr-1" />
              Reset Filters
            </Button>
          </div>
          
          {/* Results */}
          {results && (
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">
                  Results ({trueTotalResults})
                </h3>
                {results.query && (
                  <span className="text-sm text-slate-500">
                    Showing results for "{results.query}"
                  </span>
                )}
              </div>
              
              {totalResults === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="py-8 text-center">
                    <Search className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500">No results found</p>
                    <p className="text-sm text-slate-400 mt-1">Try adjusting your search criteria</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4 max-h-[300px] overflow-y-auto">
                  {CATEGORIES.map(cat => {
                    const items = results[cat.id] || [];
                    if (items.length === 0) return null;
                    
                    const Icon = cat.icon;
                    const trueTotal = results.total_counts?.[cat.id] ?? items.length;
                    return (
                      <div key={cat.id}>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={cat.color}>
                            <Icon className="w-3 h-3 mr-1" />
                            {cat.label}
                          </Badge>
                          <span className="text-xs text-slate-400">({items.length})</span>
                        </div>
                        {trueTotal > items.length && (
                          <p className="text-xs text-slate-400 -mt-1 mb-2" data-testid={`result-${cat.id}-more-note`}>
                            Showing {items.length} of {trueTotal} — refine your search to narrow results.
                          </p>
                        )}
                        <div className="space-y-1">
                          {items.map((item, idx) => (
                            <button
                              key={item.id || idx}
                              onClick={() => handleResultClick(item)}
                              className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 text-left transition-colors"
                              data-testid={`result-${cat.id}-${idx}`}
                            >
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-900 truncate">
                                  {item.job_number || item.proposal_number || item.title || item.full_name || item.company_name || item.name || 'Untitled'}
                                </p>
                                <p className="text-sm text-slate-500 truncate">
                                  {item.client_name || item.email || item.location || item.model || ''}
                                </p>
                              </div>
                              {item.status && (
                                <Badge variant="outline" className="ml-2 text-xs">
                                  {item.status}
                                </Badge>
                              )}
                              <ChevronRight className="w-4 h-4 text-slate-400 ml-2 flex-shrink-0" />
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
